// Helpers for placing Anthropic prompt-cache breakpoints on a
// MessageParam in-place. Extracted from client.ts so the placement
// logic can be unit-tested without mocking the streaming SDK.
//
// Caching mental model:
//   - `cache_control: { type: 'ephemeral' }` on a block tells the API
//     to *write* cache for the prefix ending at that block (5-min TTL).
//   - Reads happen automatically on any matching prefix — they don't
//     require cache_control on the current request. That's why the
//     "sliding" within-turn breakpoint is safe: stripping cache_control
//     from a prior tool_result doesn't evict the cache it wrote, it
//     just stops refreshing it.
//   - Hard limit: 4 cache_control per request. We cap at 4 by sliding,
//     not stacking, breakpoints inside the tool-use loop.

import type Anthropic from '@anthropic-ai/sdk';

const EPHEMERAL: Anthropic.CacheControlEphemeral = { type: 'ephemeral' };

type Mutable = { cache_control?: Anthropic.CacheControlEphemeral | null };

/** Mark the last block of a message with cache_control: ephemeral.
 *  Converts string content to a single text block when needed —
 *  per-block flags require block-array content. */
export function markBreakpoint(msg: Anthropic.MessageParam): void {
  if (typeof msg.content === 'string') {
    msg.content = [
      {
        type: 'text',
        text: msg.content,
        cache_control: EPHEMERAL,
      },
    ];
    return;
  }
  const last = msg.content[msg.content.length - 1];
  if (!last) return;
  (last as Mutable).cache_control = EPHEMERAL;
}

/** Strip cache_control from the last block of a message. No-op for
 *  string content (which can't carry cache_control anyway). */
export function clearBreakpoint(msg: Anthropic.MessageParam): void {
  if (typeof msg.content === 'string') return;
  const last = msg.content[msg.content.length - 1];
  if (!last) return;
  delete (last as Mutable).cache_control;
}
